import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import NamedBox from '../atoms/NamedBox';
import CloseButton from '../atoms/CloseButton';
import {
  COLOR_BORDER,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_PURPLE,
  COLOR_TEXT,
  COLOR_WHITE,
  COLOR_BACKGROUND_WARM,
  COLOR_BACKGROUND_LIGHT,
  COLOR_DARK_GRAY,
  COLOR_MIDDLE_GRAY,
} from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import MEDIA from '../styles/media';
import SortArrowIcon from '../icons/SortArrowIcon';

export interface PFFGameLogRow {
  id: string;
  date: string; // MM/DD/YY
  isHomeTeam?: boolean;
  opponent: string;
  opponentHref?: string;
  score?: string; // e.g. "31-17 W"
  offensive: {
    overall: number;
    pass: number;
    run: number;
    rec: number;
    runBlock: number;
    passBlock: number;
  };
  defensive: {
    overall: number;
    passRush: number;
    runDef: number;
    cover: number;
  };
  snaps: {
    total: number;
    run: number;
    pass: number;
    rec: number;
    runBlock: number;
    passBlock: number;
    passRush: number;
    passDef: number;
    cover: number;
  };
}

interface PFFGameGradeLogTableProps {
  className?: string;
  /** When provided, table is wrapped in NamedBox with this title and a close button that calls onClose */
  useNamedBox?: boolean;
  title?: string;
  onClose?: () => void;
  rows?: PFFGameLogRow[];
  /** Index or date (MM/DD/YY) of the row to highlight with purple border */
  selectedRowIndex?: number;
  selectedDate?: string;
}

const CATEGORY_COLUMNS = [
  { key: 'offensive', label: 'Offensive Grades', cols: 6 },
  { key: 'defensive', label: 'Defensive Grades', cols: 4 },
  { key: 'snaps', label: 'Snaps', cols: 9 },
] as const;

const SUB_HEADERS = [
  'Date',
  'Opponent',
  'Overall', 'Pass', 'Run', 'Rec', 'Run Block', 'Pass Block',
  'Overall', 'Pass Rush', 'Run Def.', 'Cover.',
  'Total', 'Run', 'Pass', 'Rec.', 'Run Block', 'Pass Block', 'Pass Rush', 'Pass Def.', 'Cover.',
];

const DEFAULT_ROWS: PFFGameLogRow[] = [
  // { id: '1', date: '11/29/25', opponent: '@Arkansas', opponentHref: '#', score: '31-17 W', passing: { comp: 0, att: 0, ints: 0, yds: 0, tds: 0 }, rushing: { att: 27, yds: 157, td: 1, ypa: 5.8 }, receiving: { rec: 1, yds: 5, tds: 0, ypc: 5.0 }, defense: { tkls: 0, sacks: 0, tfls: 0, ints: 0, ffs: 0, frs: 0 } },
  // { id: '2', date: '11/22/25', opponent: '@Oklahoma', opponentHref: '#', score: '6-17 L', passing: { comp: 0, att: 0, ints: 0, yds: 0, tds: 0 }, rushing: { att: 15, yds: 82, td: 0, ypa: 5.5 }, receiving: { rec: 0, yds: 0, tds: 0, ypc: 0 }, defense: { tkls: 0, sacks: 0, tfls: 0, ints: 0, ffs: 0, frs: 0 } },
  // { id: '3', date: '11/15/25', opponent: 'Mississippi St.', opponentHref: '#', score: '49-27 W', passing: { comp: 0, att: 0, ints: 0, yds: 0, tds: 0 }, rushing: { att: 22, yds: 124, td: 2, ypa: 5.6 }, receiving: { rec: 2, yds: 18, tds: 0, ypc: 9.0 }, defense: { tkls: 0, sacks: 0, tfls: 0, ints: 0, ffs: 0, frs: 0 } },
  // { id: '4', date: '11/08/25', opponent: 'Texas A&M', opponentHref: '#', score: '17-38 L', passing: { comp: 0, att: 0, ints: 0, yds: 0, tds: 0 }, rushing: { att: 18, yds: 89, td: 0, ypa: 4.9 }, receiving: { rec: 1, yds: 7, tds: 0, ypc: 7.0 }, defense: { tkls: 0, sacks: 0, tfls: 0, ints: 0, ffs: 0, frs: 0 } },
  // { id: '5', date: '10/25/25', opponent: '@Vanderbilt', opponentHref: '#', score: '10-17 L', passing: { comp: 0, att: 0, ints: 0, yds: 0, tds: 0 }, rushing: { att: 14, yds: 62, td: 0, ypa: 4.4 }, receiving: { rec: 0, yds: 0, tds: 0, ypc: 0 }, defense: { tkls: 0, sacks: 0, tfls: 0, ints: 0, ffs: 0, frs: 0 } },
  // { id: '6', date: '10/18/25', opponent: 'Alabama', opponentHref: '#', score: '23-17 W', passing: { comp: 0, att: 0, ints: 0, yds: 0, tds: 0 }, rushing: { att: 20, yds: 98, td: 1, ypa: 4.9 }, receiving: { rec: 2, yds: 22, tds: 0, ypc: 11.0 }, defense: { tkls: 0, sacks: 0, tfls: 0, ints: 0, ffs: 0, frs: 0 } },
];

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    fontFamily: FONT_PROXIMA_NOVA,
  },
  headerName: {
    fontFamily: FONT_PROXIMA_NOVA,
  },
  tableContainer: {
    maxHeight: 800,
    overflow: 'auto',
  },
  table: {
    width: '100%',
    minWidth: 960,
    borderCollapse: 'collapse' as const,
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.2,
  },
  thead: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 3,
    backgroundColor: COLOR_WHITE,
  },
  categoryRow: {},
  categoryCell: {
    borderLeft: `1px solid ${COLOR_WHITE}`,
    padding: theme.spacing(0.75, 1),
    textAlign: 'left',
    backgroundColor: COLOR_BACKGROUND_WARM,
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  categoryCellFirst: {
    textAlign: 'left' as const,
    borderLeft: `1px solid ${COLOR_BACKGROUND_WARM}`,
  },
  subHeaderRow: {},
  subHeaderCell: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    borderRight: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(1.5, 1),
    fontWeight: 700,
    color: COLOR_TEXT,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    backgroundColor: COLOR_WHITE,
    userSelect: 'none' as const,
  },
  subHeaderCellFirst: {
    textAlign: 'left' as const,
    width: 90,
  },
  subHeaderCellSecond: {
    textAlign: 'left' as const,
    width: 180,
    minWidth: 140,
  },
  sortLabel: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.25),
    overflow: 'hidden',

    '& .MuiTableSortLabel-icon': {
      margin: 0,
      fontSize: theme.typography.pxToRem(16),
      flex: '0 0 auto',
    },

    '& .MuiTableSortLabel-wrapper': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  sortIcon: {
    opacity: 1,
    color: COLOR_MIDDLE_GRAY,

    '& svg': {
      display: 'block',
      width: 9,
      height: 'auto',
    },
  },
  bodyRow: {
    backgroundColor: COLOR_BACKGROUND_LIGHT,
  },
  bodyRowAlt: {
    backgroundColor: COLOR_WHITE,
  },
  bodyRowSelected: {
    '& td': {
      borderTopColor: COLOR_PURPLE,
      borderBottomColor: COLOR_PURPLE,
      borderTopWidth: 2,
      borderBottomWidth: 2,
    },
    '& td:first-child': {
      borderLeft: `2px solid ${COLOR_PURPLE}`,
    },
    '& td:last-child': {
      borderRight: `2px solid ${COLOR_PURPLE}`,
    },
  },
  bodyCell: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    borderRight: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(1),
    color: COLOR_TEXT,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    fontWeight: 600,
  },
  bodyCellFirst: {
    textAlign: 'left' as const,
  },
  bodyCellSecond: {
    textAlign: 'left' as const,
    whiteSpace: 'normal' as const,
  },
  opponentLink: {
    color: COLOR_BLUE,
    textDecoration: 'underline',
    textAlign: 'center',
    cursor: 'pointer',

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
  scoreLine: {
    color: COLOR_TEXT,
    marginTop: theme.spacing(0.25),
  },
  footerNote: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${COLOR_BORDER}`,
    textAlign: 'center',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    color: COLOR_TEXT,
  },
}), { name: 'FullGameLogTable' });


export default function PFFGameGradeLogTable(props: PFFGameGradeLogTableProps) {
  const {
    className,
    useNamedBox = false,
    title = 'Full Game Log',
    onClose,
    rows = DEFAULT_ROWS,
    selectedRowIndex,
    selectedDate,
  } = props;
  const classes = useStyles();
  const [orderByCol, setOrderByCol] = useState<number | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((colIndex: number) => {
    const isAsc = orderByCol === colIndex && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderByCol(colIndex);
  }, [orderByCol, order]);

  function getCellValue(row: PFFGameLogRow, colIndex: number): number | string {
    if (colIndex === 0) return row.date;
    if (colIndex === 1) return row.opponent;
    if (colIndex <= 7) {
      const k = ['overall', 'pass', 'run', 'rec', 'runBlock', 'passBlock'][colIndex - 2];
      return (row.offensive as Record<string, number>)[k];
    }
    if (colIndex <= 11) {
      const k = ['overall', 'passRush', 'runDef', 'cover'][colIndex - 8];
      return (row.defensive as Record<string, number>)[k];
    }
    const k = ['total', 'run', 'pass', 'rec', 'runBlock', 'passBlock', 'passRush', 'passDef', 'cover'][colIndex - 12];
    return (row.snaps as Record<string, number>)[k];
  }

  const isSelected = useCallback((row: PFFGameLogRow, index: number) => {
    if (selectedDate != null) return row.date === selectedDate;
    if (selectedRowIndex != null) return index === selectedRowIndex;
    return false;
  }, [selectedDate, selectedRowIndex]);

  const sortedRows = React.useMemo(() => {
    if (orderByCol == null) return rows;
    return [...rows].sort((a, b) => {
      const aVal = getCellValue(a, orderByCol);
      const bVal = getCellValue(b, orderByCol);
      const cmp = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return order === 'asc' ? cmp : -cmp;
    });
  }, [rows, orderByCol, order]);

  const tableContent = (
    <>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <colgroup>
            <col style={{ width: '7%' }} />
            <col style={{ width: '17%' }} />
            {Array.from({ length: 19 }).map((_, idx) => (
              <col key={idx} style={{ width: '4%' }} />
            ))}
          </colgroup>
          <thead className={classes.thead}>
            <tr className={classes.categoryRow}>
              <th className={clsx(classes.categoryCell, classes.categoryCellFirst)} />
              <th className={clsx(classes.categoryCell, classes.categoryCellFirst)} />
              {CATEGORY_COLUMNS.map((cat) => (
                <th
                  key={cat.key}
                  className={classes.categoryCell}
                  colSpan={cat.cols}
                >
                  {cat.label}
                </th>
              ))}
            </tr>
            <tr className={classes.subHeaderRow}>
              {SUB_HEADERS.map((label, idx) => {
                const isDate = idx === 0;
                const isOpponent = idx === 1;
                return (
                  <th key={idx} className={clsx(
                    classes.subHeaderCell,
                    isDate && classes.subHeaderCellFirst,
                    isOpponent && classes.subHeaderCellSecond,
                  )}>
                    <TableSortLabel
                      active={orderByCol === idx}
                      direction={orderByCol === idx ? order : 'asc'}
                      onClick={() => handleSort(idx)}
                      className={classes.sortLabel}
                      hideSortIcon={false}
                      IconComponent={(iconProps) => (
                        <SortArrowIcon
                          {...iconProps}
                          style={{
                            color: orderByCol === idx ? COLOR_BLUE : COLOR_DARK_GRAY,
                          }}
                        />
                      )}
                      classes={{ icon: classes.sortIcon }}
                    >
                      {label}
                    </TableSortLabel>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => (
              <tr
                key={row.id}
                className={clsx(
                  classes.bodyRow,
                  index % 2 === 1 && classes.bodyRowAlt,
                  isSelected(row, index) && classes.bodyRowSelected,
                )}
              >
                <td className={clsx(classes.bodyCell, classes.bodyCellFirst)}>{row.date}</td>
                <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                  {row.opponentHref ? (
                    <a href={row.opponentHref} className={classes.opponentLink}>{row?.isHomeTeam ? '' : '@'}{row.opponent}</a>
                  ) : (
                    <span>{row?.isHomeTeam ? '' : '@ '}{row.opponent}</span>
                  )}
                  {row.score != null && (
                    <div className={classes.scoreLine}>{row.score}</div>
                  )}
                </td>
                <td className={classes.bodyCell}>{row.offensive.overall}</td>
                <td className={classes.bodyCell}>{row.offensive.pass}</td>
                <td className={classes.bodyCell}>{row.offensive.run}</td>
                <td className={classes.bodyCell}>{row.offensive.rec}</td>
                <td className={classes.bodyCell}>{row.offensive.runBlock}</td>
                <td className={classes.bodyCell}>{row.offensive.passBlock}</td>
                <td className={classes.bodyCell}>{row.defensive.overall}</td>
                <td className={classes.bodyCell}>{row.defensive.passRush}</td>
                <td className={classes.bodyCell}>{row.defensive.runDef}</td>
                <td className={classes.bodyCell}>{row.defensive.cover}</td>
                <td className={classes.bodyCell}>{row.snaps.total}</td>
                <td className={classes.bodyCell}>{row.snaps.run}</td>
                <td className={classes.bodyCell}>{row.snaps.pass}</td>
                <td className={classes.bodyCell}>{row.snaps.rec}</td>
                <td className={classes.bodyCell}>{row.snaps.runBlock}</td>
                <td className={classes.bodyCell}>{row.snaps.passBlock}</td>
                <td className={classes.bodyCell}>{row.snaps.passRush}</td>
                <td className={classes.bodyCell}>{row.snaps.passDef}</td>
                <td className={classes.bodyCell}>{row.snaps.cover}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={classes.footerNote}>
        <strong>NOTE:</strong> Only Games involving FBS Teams (2005 to present), FCS Games (2022 to present), and DII/DIII Games (2022 to present) are shown in the Game Log.
      </div>
    </>
  );

  if (useNamedBox) {
    return (
      <NamedBox
        className={clsx(classes.root, className)}
        headerNameClassName={classes.headerName}
        name={title}
        headerContent={onClose != null ? <CloseButton onClick={onClose} /> : undefined}
        white
      >
        {tableContent}
      </NamedBox>
    );
  }

  return (
    <div className={clsx(classes.root, className)}>
      {tableContent}
    </div>
  );
}
